import { useSnackbar } from 'notistack';
import { useApi } from '../../shared/context';
import { TermSheet } from '../../shared/models';
import { useMutation, useQueryClient } from 'react-query';
import { Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle } from '@mui/material';
import { CerebroTextButton } from '../../shared/components';
import { getResponseData } from '../../shared/utils/axios';
import { QueryKeys } from '../../shared/hooks';
import { CerebroApiEndpoints } from '../../shared/utils/api';

interface DeleteDebtTermListItemDialogProps {
	dealGuid: string | undefined;
	debtTerm: TermSheet | null;
	close: () => void;
}

export const DeleteDebtTermListItemDialog: React.FC<DeleteDebtTermListItemDialogProps> = ({
	dealGuid,
	debtTerm,
	close,
}) => {
	const api = useApi();
	const snackbar = useSnackbar();
	const client = useQueryClient();

	const onDeleteConfirmation = () => {
		if (debtTerm !== null) deleteExistingDebtTerm(debtTerm);
	};

	const { mutate: deleteExistingDebtTerm } = useMutation<void, unknown, TermSheet>(
		termToDelete =>
			getResponseData(api.delete<void>(CerebroApiEndpoints.TermSheets.Specific(dealGuid, termToDelete.guid))),
		{
			onMutate: () => {
				close();
			},
			onSuccess: () => {
				client.invalidateQueries(QueryKeys.TermSheets.ExistingTerms(dealGuid));
				snackbar.enqueueSnackbar('Existing debt term successfully deleted.', { variant: 'success' });
				close();
			},
			onError: () => {
				snackbar.enqueueSnackbar('There was a problem deleting the existing debt term.', {
					variant: 'error',
				});
			},
		}
	);

	return (
		<Dialog open={debtTerm !== null} onClose={close}>
			<DialogTitle>Confirm Delete</DialogTitle>
			<DialogContent>
				<DialogContentText>
					Are you sure you want to delete the debt term for {debtTerm?.bankName}?
				</DialogContentText>
			</DialogContent>
			<DialogActions>
				<CerebroTextButton onClick={close}>No</CerebroTextButton>
				<CerebroTextButton onClick={onDeleteConfirmation}>Yes</CerebroTextButton>
			</DialogActions>
		</Dialog>
	);
};
